import { Select, Skeleton } from 'antd';
import { type FC, useMemo } from 'react';
import type { DefaultOptionType, SelectProps } from 'antd/lib/select';
import { omit } from 'ramda';
import { filterOption, filterSort } from '../utils';
import { useInventoryLocations } from '../../../api/hooks/inventory-location/useFind';
import { mapListToIdSelectOptions } from '../../../utils/mapListToIdSelectOptions';
import type { FindInventoryLocationsArg } from '../../../lib/services';

type InventoryLocationOption = Pick<DefaultOptionType, 'value' | 'label'>;

export interface InventoryLocationSelectProps extends SelectProps<number> {
  filter?: FindInventoryLocationsArg;
  onLocationChange?: (value: number | null, option: InventoryLocationOption) => void;
  onChange?: (value: number | null) => void;
}

export const InventoryLocationSelect: FC<InventoryLocationSelectProps> = ({
  filter = {},
  onChange,
  onLocationChange,
  ...propsToPass
}) => {
  const { data: { data: inventoryLocations = [] } = {}, isLoading } = useInventoryLocations({
    ...omit(['page', 'pageSize'], filter),
    fields: ['id', 'name'],
    limit: -1,
    start: 0,
  });

  const locationOptions = useMemo(() => mapListToIdSelectOptions(inventoryLocations), [inventoryLocations]) ?? [];

  const handleChange = (id?: number, option?: InventoryLocationOption) => {
    onChange?.(id ?? null);
    onLocationChange?.(id ?? null, option);
  };

  if (isLoading) return <Skeleton.Input active block />;

  return (
    <Select
      allowClear
      showSearch={{ filterOption: filterOption, filterSort: filterSort }}
      loading={isLoading}
      options={locationOptions}
      popupMatchSelectWidth={false}
      onChange={handleChange}
      placeholder="Select inventory location"
      {...propsToPass}
    />
  );
};
